import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Input, FormGroup, Label, Form, Row, Col, Card, CardBody, Badge } from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { FiDollarSign, FiCalendar, FiUser, FiSave, FiArrowLeft } from "react-icons/fi";

const EditStaffSalary = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [staffList, setStaffList] = useState([]);
    const [formData, setFormData] = useState({
        id: id,
        staff_id: "",
        year: "",
        month: "",
        base_salary: "",
        bonus: 0,
        deduction: 0,
        payment_status: "pending",
        paid_date: "",
        payment_mode: "cash",
        note: ""
    });

    const [errors, setErrors] = useState({});

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '50px',
            borderRadius: '10px',
            borderColor: state.isFocused ? '#f16436' : '#dee2e6',
            boxShadow: 'none',
            backgroundColor: '#f8f9fa',
            '&:hover': { borderColor: '#f16436' }
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    useEffect(() => {
        const initPage = async () => {
            dispatch(loadingTrue());
            await fetchStaff();
            await fetchSalaryDetails();
            dispatch(loadingFalse());
        };
        initPage();
    }, [id]);

    const fetchStaff = async () => {
        try {
            const res = await Handler({ method: "GET", url: "/staff/list.php" });
            if (res?.success) {
                setStaffList(res.data.map(s => ({ value: s.id, label: s.name?.toUpperCase() })));
            }
        } catch (err) { toast.error("Error loading staff list"); }
    };

    const fetchSalaryDetails = async () => {
        try {
            const res = await Handler({ method: "GET", url: `/salary/view.php?id=${id}` });
            if (res?.success) {
                const staffData = res.data.staff;
                const salaryData = res.data.salary;
                const paymentData = res.data.payment;
                const noteData = res.data.note;

                setFormData({
                    id: salaryData.id,
                    staff_id: staffData.id,
                    year: salaryData.year,
                    month: salaryData.month,
                    base_salary: salaryData.base_salary,
                    bonus: salaryData.bonus,
                    deduction: salaryData.deduction,
                    payment_status: paymentData.status,
                    paid_date: paymentData.paid_on || "",
                    payment_mode: paymentData.payment_mode || "cash",
                    note: noteData || ""
                });
            }
        } catch (err) { toast.error("Failed to load salary record"); }
    };

    // 🔥 Separate Validation Logic
    const validateForm = () => {
        let formErrors = {};
        let isValid = true;

        if (!formData.staff_id) {
            formErrors.staff_id = "Staff member is required";
            isValid = false;
        }

        if (!formData.base_salary || formData.base_salary <= 0) {
            formErrors.base_salary = "Valid base salary is required";
            isValid = false;
        }

        if (formData.payment_status === "paid" && !formData.paid_date) {
            formErrors.paid_date = "Payment date is mandatory for disbursed status";
            isValid = false;
        }

        setErrors(formErrors);
        if (!isValid) toast.error("Please fill all required fields correctly!");
        return isValid;
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        
        // 🔥 Rounded Positive Number Logic
        if (type === "number") {
            let val = value === "" ? "" : Math.round(Math.abs(parseFloat(value)));
            setFormData({ ...formData, [name]: val });
        } else {
            setFormData({ ...formData, [name]: value });
        }
        if (errors[name]) setErrors({ ...errors, [name]: "" });
    };

    const handleSelectChange = (name, value) => {
        setFormData({ ...formData, [name]: value });
        if (errors[name]) setErrors({ ...errors, [name]: "" });
    };

    const netSalary = (parseFloat(formData.base_salary || 0) +
        parseFloat(formData.bonus || 0)) -
        parseFloat(formData.deduction || 0);

    const monthOptions = Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: new Date(0, i).toLocaleString('en-US', { month: 'long' })
    }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        dispatch(loadingTrue());
        try {
            const res = await Handler({
                method: "PUT",
                url: "/salary/update.php",
                data: formData
            });

            if (res?.success) {
                Swal.fire("Updated!", "Salary record has been saved.", "success");
                navigate("/salary");
                dispatch(loadingFalse());
            } else {
                if (res?.errors) setErrors(res.errors);
                else toast.error(res?.message || "Update failed");
                dispatch(loadingFalse());
            }
        } catch (err) {
            Swal.fire("Error", "Server error occurred", "error");
            dispatch(loadingFalse());
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <div className="page-container p-3 bg-light min-vh-100">
            <div className="page-header mb-4 d-md-flex justify-content-between align-items-center">
                <div>
                    <h3 className="fw-bold text-dark m-0">Edit Staff Salary</h3>
                    <p className="text-muted small m-0">Modifying record ID: <Badge color="soft-primary" className="text-primary">#{id}</Badge></p>
                </div>
                <Button color="white" className="shadow-sm border rounded-pill px-4 d-flex align-items-center gap-2" onClick={() => navigate(-1)}>
                    <FiArrowLeft /> Back
                </Button>
            </div>

            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="bg-primary p-1"></div>
                <CardBody className="p-4 p-md-5">
                    <Form onSubmit={handleSubmit}>
                        <Row className="g-4">
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="small fw-bold text-uppercase text-muted"><FiUser className="me-1"/> Staff Member *</Label>
                                    <Select
                                        options={staffList}
                                        styles={customSelectStyles}
                                        value={staffList.find(s => s.value == formData.staff_id)}
                                        onChange={(opt) => handleSelectChange("staff_id", opt?.value)}
                                        menuPortalTarget={document.body}
                                    />
                                    {errors.staff_id && <small className="text-danger">{errors.staff_id}</small>}
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label className="small fw-bold text-uppercase text-muted">Year</Label>
                                    <Input type="number" name="year" className="bg-light border-0 py-2 rounded-3" style={{ height: '50px' }} value={formData.year} onChange={handleChange} />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label className="small fw-bold text-uppercase text-muted">Month</Label>
                                    <Select
                                        options={monthOptions}
                                        styles={customSelectStyles}
                                        value={monthOptions.find(m => m.value == formData.month)}
                                        onChange={(opt) => handleSelectChange("month", opt?.value)}
                                        menuPortalTarget={document.body}
                                    />
                                </FormGroup>
                            </Col>

                            <Col md={4}>
                                <FormGroup>
                                    <Label className="small fw-bold text-uppercase text-muted">Base Salary (₹) *</Label>
                                    <Input type="number" name="base_salary" className={`bg-light border-0 py-2 rounded-3 ${errors.base_salary ? "is-invalid" : ""}`} style={{ height: '50px' }} value={formData.base_salary} onChange={handleChange} />
                                    {errors.base_salary && <small className="text-danger">{errors.base_salary}</small>}
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label className="small fw-bold text-uppercase text-muted">Bonus (₹)</Label>
                                    <Input type="number" name="bonus" className="bg-light border-0 py-2 rounded-3" style={{ height: '50px' }} value={formData.bonus} onChange={handleChange} />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label className="small fw-bold text-uppercase text-muted">Deduction (₹)</Label>
                                    <Input type="number" name="deduction" className="bg-light border-0 py-2 rounded-3" style={{ height: '50px' }} value={formData.deduction} onChange={handleChange} />
                                </FormGroup>
                            </Col>
                        </Row>

                        <div className="bg-light p-4 rounded-4 my-4 d-flex justify-content-between align-items-center border border-dashed border-primary">
                            <div>
                                <h6 className="text-primary text-uppercase small fw-bold mb-1">Adjusted Net Salary</h6>
                                <p className="small text-muted m-0 italic">Calculated based on updated figures.</p>
                            </div>
                            <h2 className="fw-bold text-dark m-0">₹ {Math.max(0, Math.round(netSalary))?.toLocaleString()}</h2>
                        </div>

                        <Row className="g-4">
                            <Col md={4}>
                                <FormGroup>
                                    <Label className="small fw-bold text-uppercase text-muted">Status</Label>
                                    <Select
                                        options={[{ value: "pending", label: "Pending" }, { value: "paid", label: "Paid" }]}
                                        styles={customSelectStyles}
                                        value={[{ value: "pending", label: "Pending" }, { value: "paid", label: "Paid" }].find(s => s.value === formData.payment_status)}
                                        onChange={(opt) => handleSelectChange("payment_status", opt?.value)}
                                        menuPortalTarget={document.body}
                                    />
                                </FormGroup>
                            </Col>

                            {formData.payment_status === "paid" && (
                                <>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label className="small fw-bold text-uppercase text-muted"><FiCalendar className="me-1"/> Disbursement Date *</Label>
                                            <Input type="date" name="paid_date" className={`bg-light border-0 py-2 rounded-3 ${errors.paid_date ? "is-invalid" : ""}`} style={{ height: '50px' }} value={formData.paid_date} onChange={handleChange} />
                                            {errors.paid_date && <small className="text-danger">{errors.paid_date}</small>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label className="small fw-bold text-uppercase text-muted">Payment Mode</Label>
                                            <Select
                                                options={[{ value: "cash", label: "Cash" }, { value: "bank_transfer", label: "Bank" }, { value: "upi", label: "UPI" }]}
                                                styles={customSelectStyles}
                                                value={[{ value: "cash", label: "Cash" }, { value: "bank_transfer", label: "Bank" }, { value: "upi", label: "UPI" }].find(m => m.value === formData.payment_mode)}
                                                onChange={(opt) => handleSelectChange("payment_mode", opt?.value)}
                                                menuPortalTarget={document.body}
                                            />
                                        </FormGroup>
                                    </Col>
                                </>
                            )}
                        </Row>

                        <FormGroup className="mt-4">
                            <Label className="small fw-bold text-uppercase text-muted">Internal Remarks</Label>
                            <Input type="textarea" name="note" value={formData.note} onChange={handleChange} rows="2" className="bg-light border-0 rounded-3" placeholder="Add specific details..." />
                        </FormGroup>

                        <div className="mt-5 pt-4 border-top d-flex justify-content-end gap-3">
                            <Button type="button" color="light" className="px-4 py-2 rounded-3 border" onClick={() => navigate(-1)}>Discard</Button>
                            <Button type="submit" color="primary" className="px-5 py-2 rounded-3 shadow-sm fw-bold d-flex align-items-center gap-2">
                                <FiSave /> Save Changes
                            </Button>
                        </div>
                    </Form>
                </CardBody>
            </Card>
        </div>
    );
};

export default EditStaffSalary;